/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect } from 'react';
import { type EREProvider } from '../types';
import { PROVIDER_LIST_JSONLD_ID, buildProviderListJsonLd } from '../lib/pageSchema.js';

/**
 * Emits ItemList + Service + Dataset JSON-LD for the provider comparison on the
 * homepage. Each visible provider becomes a Service item with its commission as
 * an Offer (no AggregateRating since the ratings left, 2026-10-14). The
 * accompanying Dataset node points LLM/agent crawlers at the
 * machine-readable JSON feed (/api/providers) so they can consume the live
 * comparison data instead of scraping the DOM — see public/llms.txt.
 *
 * The payload builder is shared with the server bake (src/lib/pageSchema.js):
 * server/seoMeta.ts and scripts/prerender.mjs write the same
 * `#jsonld-providerlist` script into the homepage head — the crawlers this
 * graph is FOR do not run JavaScript, so a client-only node reached none of
 * them — and this hook takes it over in place after hydration: same id, same
 * bytes, never a duplicate.
 */
export function useProviderListSchema(providers: EREProvider[]) {
  useEffect(() => {
    const payload = buildProviderListJsonLd(providers);

    if (!payload) {
      document.getElementById(PROVIDER_LIST_JSONLD_ID)?.remove();
      return;
    }

    let el = document.getElementById(PROVIDER_LIST_JSONLD_ID) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = PROVIDER_LIST_JSONLD_ID;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);

    return () => {
      document.getElementById(PROVIDER_LIST_JSONLD_ID)?.remove();
    };
  }, [providers]);
}
